import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText,
  MessageSquare,
  Package,
  DollarSign,
  TrendingUp,
  Clock,
  CheckCircle,
  Star,
  ChevronRight,
  Building,
  Shield,
  AlertCircle,
  Plus,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'sonner';
import { useCountry } from '../context/CountryContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Available RFQs Card Component
const AvailableRFQCard = ({ rfq, currencySymbol, onQuote }) => {
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`available-rfq-${rfq.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-mono text-gray-500">{rfq.rfq_number}</p>
            <h3 className="font-semibold">{rfq.title}</h3>
          </div>
          <Badge className="bg-blue-100 text-blue-700">New RFQ</Badge>
        </div>
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{rfq.item_details}</p>
        
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span className="flex items-center gap-1">
            <Package className="w-4 h-4" />
            {rfq.quantity} {rfq.quantity_unit}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {new Date(rfq.created_at).toLocaleDateString()}
          </span>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="text-sm">
            <span className="text-gray-500">Location: </span>
            <span className="font-medium">{rfq.delivery_location}</span>
          </div>
          <Button size="sm" onClick={() => onQuote(rfq)} data-testid={`quote-btn-${rfq.id}`}>
            <Plus className="w-4 h-4 mr-1" />
            Submit Quote
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

// My Quote Card Component
const MyQuoteCard = ({ quoteData, currencySymbol, onView, onWithdraw }) => {
  const { quote, rfq_number, rfq_title, rfq_status } = quoteData;
  
  const getStatusBadge = (status) => {
    const statusConfig = {
      submitted: { label: 'Pending Review', className: 'bg-yellow-100 text-yellow-700' },
      shortlisted: { label: 'Shortlisted! 🎉', className: 'bg-green-100 text-green-700' },
      accepted: { label: 'Won! 🏆', className: 'bg-green-500 text-white' },
      rejected: { label: 'Not Selected', className: 'bg-red-100 text-red-700' },
      not_selected: { label: 'Not Selected', className: 'bg-gray-100 text-gray-600' },
      withdrawn: { label: 'Withdrawn', className: 'bg-gray-100 text-gray-500' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };
  
  return (
    <Card className={`hover:shadow-md transition-shadow ${quote.status === 'accepted' ? 'border-2 border-green-500 bg-green-50/30' : ''} ${quote.status === 'shortlisted' ? 'border-2 border-yellow-400 bg-yellow-50/30' : ''}`} data-testid={`my-quote-${quote.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-mono text-gray-500">{quote.quote_number}</p>
            <h3 className="font-semibold">{rfq_title}</h3>
            <p className="text-xs text-gray-500">RFQ: {rfq_number}</p>
          </div>
          {getStatusBadge(quote.status)}
        </div>
        
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-3 mb-3">
          <p className="text-sm text-gray-500">Your Quote</p>
          <p className="text-2xl font-bold text-blue-700">
            {currencySymbol}{quote.quote_amount?.toLocaleString()}
          </p>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span>Delivery: {quote.delivery_time?.replace('_', '-')}</span>
          <span>Valid until: {new Date(quote.valid_until).toLocaleDateString()}</span>
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="text-sm text-gray-500">
            RFQ Status: <span className="font-medium capitalize">{rfq_status}</span>
          </div>
          <div className="flex gap-2">
            {quote.status === 'submitted' && (
              <Button 
                variant="outline" 
                size="sm" 
                className="text-red-600"
                onClick={() => onWithdraw(quote)}
                data-testid={`withdraw-${quote.id}`}
              >
                Withdraw
              </Button>
            )}
            {quote.status === 'accepted' && (
              <Button size="sm" className="bg-green-600" onClick={() => onView(quoteData)}>
                View Order
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Booking Card Component
const BookingCard = ({ booking, currencySymbol, onUpdateStatus }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      confirmed: { label: 'Confirmed', className: 'bg-blue-100 text-blue-700' },
      processing: { label: 'Processing', className: 'bg-yellow-100 text-yellow-700' },
      shipped: { label: 'Shipped', className: 'bg-purple-100 text-purple-700' },
      delivered: { label: 'Delivered', className: 'bg-green-100 text-green-700' },
      completed: { label: 'Completed', className: 'bg-gray-100 text-gray-700' },
    };
    const config = statusConfig[status] || { label: status, className: 'bg-gray-100' };
    return <Badge className={config.className}>{config.label}</Badge>;
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow" data-testid={`booking-${booking.id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <p className="text-sm font-mono text-gray-500">{booking.booking_number}</p>
            <h3 className="font-semibold">Order from {booking.customer_name}</h3>
          </div>
          {getStatusBadge(booking.status)}
        </div>
        
        <p className="text-sm text-gray-600 line-clamp-2 mb-3">{booking.item_details}</p>
        
        <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 mb-3">
          <p className="text-sm text-gray-500">Order Value</p>
          <p className="text-2xl font-bold text-green-700">
            {currencySymbol}{booking.total_amount?.toLocaleString()}
          </p>
        </div>
        
        <div className="flex items-center gap-4 text-sm text-gray-500 mb-3">
          <span>{booking.quantity} {booking.quantity_unit}</span>
          {booking.delivery_date && (
            <span>Deliver by: {new Date(booking.delivery_date).toLocaleDateString()}</span>
          )}
        </div>
        
        <div className="flex items-center justify-between pt-3 border-t">
          <div className="text-sm">
            <span className="text-gray-500">Created: </span>
            {new Date(booking.created_at).toLocaleDateString()}
          </div>
          {booking.status !== 'completed' && booking.status !== 'delivered' && (
            <Button 
              size="sm" 
              onClick={() => onUpdateStatus(booking)}
              data-testid={`update-status-${booking.id}`}
            >
              Update Status
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default function ProviderDashboard() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { country } = useCountry();
  
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dashboardData, setDashboardData] = useState(null);
  const [availableRfqs, setAvailableRfqs] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [myBookings, setMyBookings] = useState([]);
  
  // Get provider email from URL or localStorage
  const providerEmail = searchParams.get('email') || localStorage.getItem('providerEmail') || '';
  const currencySymbol = country === 'UK' ? '£' : 'Rs.';
  
  useEffect(() => {
    if (providerEmail) {
      fetchDashboard();
    } else {
      setLoading(false);
    }
  }, [providerEmail]);
  
  useEffect(() => {
    if (providerEmail && activeTab === 'rfqs') {
      fetchAvailableRFQs();
    } else if (providerEmail && activeTab === 'quotes') {
      fetchMyQuotes();
    } else if (providerEmail && activeTab === 'bookings') {
      fetchMyBookings();
    }
  }, [activeTab, providerEmail]);
  
  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/provider/dashboard?provider_email=${encodeURIComponent(providerEmail)}`
      );
      const data = await response.json();
      
      if (data.success) {
        setDashboardData(data);
      } else {
        toast.error('Failed to load dashboard');
      }
    } catch (error) {
      console.error('Failed to fetch dashboard:', error);
      toast.error('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchAvailableRFQs = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/provider/available-rfqs?provider_email=${encodeURIComponent(providerEmail)}`
      );
      const data = await response.json();
      if (data.success) {
        setAvailableRfqs(data.rfqs || []);
      }
    } catch (error) {
      console.error('Failed to fetch RFQs:', error);
    }
  };
  
  const fetchMyQuotes = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/provider/my-quotes?provider_email=${encodeURIComponent(providerEmail)}`
      );
      const data = await response.json();
      if (data.success) {
        setMyQuotes(data.quotes || []);
      }
    } catch (error) {
      console.error('Failed to fetch quotes:', error);
    }
  };
  
  const fetchMyBookings = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/provider/my-bookings?provider_email=${encodeURIComponent(providerEmail)}`
      );
      const data = await response.json();
      if (data.success) {
        setMyBookings(data.bookings || []);
      }
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
    }
  };
  
  const handleSubmitQuote = (rfq) => {
    navigate(`/provider/submit-quote/${rfq.id}?email=${encodeURIComponent(providerEmail)}`);
  };
  
  const handleWithdrawQuote = async (quote) => {
    if (!confirm('Are you sure you want to withdraw this quote?')) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/provider/quotes/${quote.id}/withdraw?provider_email=${encodeURIComponent(providerEmail)}`,
        { method: 'PUT' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success('Quote withdrawn');
        fetchMyQuotes();
      } else {
        toast.error(data.detail || 'Failed to withdraw quote');
      }
    } catch (error) {
      toast.error('Failed to withdraw quote');
    }
  };
  
  const handleUpdateBookingStatus = async (booking) => {
    const nextStatus = {
      confirmed: 'processing',
      processing: 'shipped',
      shipped: 'delivered',
    };
    
    const newStatus = nextStatus[booking.status];
    if (!newStatus) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/business-services/bookings/${booking.id}/status?status=${newStatus}`,
        { method: 'PUT' }
      );
      const data = await response.json();
      if (data.success) {
        toast.success(`Status updated to ${newStatus}`);
        fetchMyBookings();
      } else {
        toast.error('Failed to update status');
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };
  
  if (!providerEmail) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-4xl mx-auto">
          <Card className="p-8 text-center">
            <Building className="w-16 h-16 mx-auto mb-4 text-gray-400" />
            <h2 className="text-xl font-semibold mb-2">Provider Dashboard</h2>
            <p className="text-gray-500 mb-4">Please sign in as a provider to access the dashboard.</p>
            <Button onClick={() => navigate('/login')}>Sign In</Button>
          </Card>
        </div>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-6xl mx-auto">
          <div className="animate-pulse space-y-4">
            <div className="h-10 bg-gray-200 rounded w-1/3"></div>
            <div className="grid grid-cols-4 gap-4">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="h-24 bg-gray-200 rounded"></div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  const provider = dashboardData?.provider;
  const stats = dashboardData?.stats;
  
  return (
    <div className="min-h-screen bg-gray-50 p-6" data-testid="provider-dashboard">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-blue-600 flex items-center justify-center text-white text-xl font-bold">
              {provider?.name?.charAt(0) || 'P'}
            </div>
            <div>
              <h1 className="text-2xl font-bold">{provider?.business_name || provider?.name}</h1>
              <div className="flex items-center gap-2 text-sm text-gray-500">
                {provider?.verified && (
                  <Badge className="bg-green-100 text-green-700">
                    <Shield className="w-3 h-3 mr-1" />
                    Verified
                  </Badge>
                )}
                <span className="flex items-center gap-1">
                  <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                  {provider?.rating || 0} ({provider?.review_count || 0} reviews)
                </span>
              </div>
            </div>
          </div>
          <Button variant="outline" onClick={fetchDashboard}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
        
        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
            <CardContent className="p-4 text-center">
              <FileText className="w-8 h-8 mx-auto mb-2 text-blue-600" />
              <p className="text-3xl font-bold text-blue-700">{stats?.available_rfqs || 0}</p>
              <p className="text-sm text-blue-600">Available RFQs</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-yellow-50 to-yellow-100 border-yellow-200">
            <CardContent className="p-4 text-center">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-yellow-600" />
              <p className="text-3xl font-bold text-yellow-700">{stats?.pending_quotes || 0}</p>
              <p className="text-sm text-yellow-600">Pending Quotes</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-50 to-green-100 border-green-200">
            <CardContent className="p-4 text-center">
              <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-600" />
              <p className="text-3xl font-bold text-green-700">{stats?.accepted_quotes || 0}</p>
              <p className="text-sm text-green-600">Won Quotes</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-50 to-purple-100 border-purple-200">
            <CardContent className="p-4 text-center">
              <DollarSign className="w-8 h-8 mx-auto mb-2 text-purple-600" />
              <p className="text-3xl font-bold text-purple-700">
                {currencySymbol}{(stats?.total_revenue || 0).toLocaleString()}
              </p>
              <p className="text-sm text-purple-600">Total Revenue</p>
            </CardContent>
          </Card>
        </div>
        
        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-4">
            <TabsTrigger value="overview" data-testid="tab-overview">
              <TrendingUp className="w-4 h-4 mr-2" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="rfqs" data-testid="tab-rfqs">
              <FileText className="w-4 h-4 mr-2" />
              Available RFQs
              {stats?.available_rfqs > 0 && (
                <Badge className="ml-2 bg-blue-500">{stats.available_rfqs}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="quotes" data-testid="tab-quotes">
              <MessageSquare className="w-4 h-4 mr-2" />
              My Quotes
            </TabsTrigger>
            <TabsTrigger value="bookings" data-testid="tab-bookings">
              <Package className="w-4 h-4 mr-2" />
              My Orders
              {stats?.active_bookings > 0 && (
                <Badge className="ml-2 bg-green-500">{stats.active_bookings}</Badge>
              )}
            </TabsTrigger>
          </TabsList>
          
          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle>Quick Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('rfqs')}
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Browse Available RFQs
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('quotes')}
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    View My Quotes
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                  <Button 
                    className="w-full justify-start" 
                    variant="outline"
                    onClick={() => setActiveTab('bookings')}
                  >
                    <Package className="w-4 h-4 mr-2" />
                    Manage Orders
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  </Button>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader>
                  <CardTitle>Performance Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Total Quotes Submitted</span>
                    <span className="font-bold">{stats?.total_quotes || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Win Rate</span>
                    <span className="font-bold text-green-600">
                      {stats?.total_quotes > 0 
                        ? Math.round((stats?.accepted_quotes / stats?.total_quotes) * 100)
                        : 0}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Active Orders</span>
                    <span className="font-bold">{stats?.active_bookings || 0}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-500">Completed Orders</span>
                    <span className="font-bold">{(stats?.total_bookings || 0) - (stats?.active_bookings || 0)}</span>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
          
          {/* Available RFQs Tab */}
          <TabsContent value="rfqs">
            {availableRfqs.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {availableRfqs.map(rfq => (
                  <AvailableRFQCard 
                    key={rfq.id} 
                    rfq={rfq} 
                    currencySymbol={currencySymbol}
                    onQuote={handleSubmitQuote}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <FileText className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Available RFQs</h3>
                <p className="text-gray-500">
                  There are no RFQs matching your services at the moment.
                  Check back later for new opportunities.
                </p>
              </Card>
            )}
          </TabsContent>
          
          {/* My Quotes Tab */}
          <TabsContent value="quotes">
            {myQuotes.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myQuotes.map(quoteData => (
                  <MyQuoteCard 
                    key={quoteData.quote.id} 
                    quoteData={quoteData} 
                    currencySymbol={currencySymbol}
                    onView={(data) => navigate(`/bookings/${data.booking_id}`)}
                    onWithdraw={handleWithdrawQuote}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <MessageSquare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Quotes Yet</h3>
                <p className="text-gray-500 mb-4">
                  You haven't submitted any quotes yet.
                  Browse available RFQs to start quoting.
                </p>
                <Button onClick={() => setActiveTab('rfqs')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Browse RFQs
                </Button>
              </Card>
            )}
          </TabsContent>
          
          {/* My Bookings/Orders Tab */}
          <TabsContent value="bookings">
            {myBookings.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {myBookings.map(booking => (
                  <BookingCard 
                    key={booking.id} 
                    booking={booking} 
                    currencySymbol={currencySymbol}
                    onUpdateStatus={handleUpdateBookingStatus}
                  />
                ))}
              </div>
            ) : (
              <Card className="p-12 text-center">
                <Package className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                <h3 className="text-lg font-semibold mb-2">No Orders Yet</h3>
                <p className="text-gray-500">
                  You don't have any orders yet.
                  Win quotes to get orders!
                </p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
